/**
 * Creator Weekly API
 * 主播招募活动相关接口
 */
import { apiClient } from '@/lib/api';
import type { ApplyFormData } from '@/app/creator-weekly/components/apply-modal';

// ============ 类型定义 ============

/** 奖励模式（后端派生） */
export type RewardMode = 'usdt' | 'points' | 'usdt_points';

/** 奖金档位（来自 currentWeek.prizeTiers） */
export interface PrizeTier {
  /** 档位：1=Top1，2=Top2，3=Top3，4=Top4-5，5=Top6-10 */
  tier: number;
  /** 前端展示文案，如 "Top 1" / "Top 4-5" */
  rankLabel: string;
  /** USDT 金额，档位未启用 USDT 时为 null */
  usdtAmount: number | null;
  /** 积分数量，档位未启用积分时为 null */
  pointsAmount: number | null;
  /** 奖励模式 */
  rewardMode: RewardMode;
}

/** 当前周活动信息 */
export interface WeekInfo {
  weekConfigId: number;
  weekNo: number;
  prizePool: number;
  startDate: string;
  endDate: string;
  endTimestamp: number;
  active: boolean;
  /** 奖金档位配置，未配置时后端返回空数组 */
  prizeTiers?: PrizeTier[];
}

/** 用户仪表盘数据 */
export interface DashboardData {
  inviteCode: string;
  invitedUsers: number;
  weeklyReward: number;
  totalReward: number;
  hasApplied: boolean;
}

/** 排行榜条目 */
export interface RankEntry {
  ranking: number;
  anchorName: string;
  anchorAvatar: string;
  /** 已废弃：后端固定返回 null，仅作兼容 */
  anchorScore: number | null;
  /** USDT 奖励（lastWeek 单周值；allTime 跨周 SUM） */
  anchorReward: number;
  /** 积分奖励（lastWeek 单周值；allTime 跨周 SUM）；未启用积分时为 null */
  pointsReward?: number | null;
  /** 奖励模式：usdt / points / usdt_points */
  rewardMode?: RewardMode;
  userId?: number;
  /** 主播 handle（预留） */
  handle?: string;
}

/** 申请记录 */
export interface ApplicationRecord {
  id: number;
  userId: number;
  userName: string;
  weekConfigId: number;
  telegram: string;
  twitterUrl: string;
  predictionUrl: string;
  predictionTag: string;
  livePlan: string;
  has50PlusFollowers: number;
  acceptEvaluation: number;
  weekNo: number;
  status: number; // 0-待审核 1-已通过 2-已拒绝
  inviterId?: string;
  createTime: string;
  updateTime: string;
}

export type RankingType = 'lastWeek' | 'allTime';

/** 排行榜接口响应（新格式） */
export interface RankingResult {
  weekConfigId: number | null;
  weekNo: number | null;
  startDate: string | null;
  endDate: string | null;
  list: RankEntry[];
}

// ============ API 调用 ============

/** 获取当前周活动信息（公开接口） */
export async function fetchCurrentWeek(): Promise<WeekInfo | null> {
  try {
    const res = await apiClient.get<WeekInfo>('/app/anchorRecruit/currentWeek');
    return res.data ?? null;
  } catch {
    return null;
  }
}

/** 获取用户仪表盘数据（需登录） */
export async function fetchDashboard(): Promise<DashboardData | null> {
  try {
    const res = await apiClient.get<DashboardData>('/app/anchorRecruit/application/dashboard');
    return res.data ?? null;
  } catch {
    return null;
  }
}

/** 获取排行榜 Top10 */
export async function fetchRanking(type: RankingType): Promise<RankingResult> {
  const empty: RankingResult = { weekConfigId: null, weekNo: null, startDate: null, endDate: null, list: [] };
  try {
    const res = await apiClient.get<RankingResult>('/app/anchorRecruit/ranking', { type });
    const d = res.data;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      return {
        weekConfigId: d.weekConfigId ?? null,
        weekNo: d.weekNo ?? null,
        startDate: d.startDate ?? null,
        endDate: d.endDate ?? null,
        list: Array.isArray(d.list) ? d.list : [],
      };
    }
    return empty;
  } catch {
    return empty;
  }
}

/** 提交主播申请 */
export async function submitApplication(data: ApplyFormData) {
  return apiClient.post('/app/anchorRecruit/application/submit', data);
}

/** 查询我的申请记录 */
export async function fetchMyApplications(): Promise<ApplicationRecord[]> {
  try {
    const res = await apiClient.get<ApplicationRecord[]>('/app/anchorRecruit/application/my');
    return res.data ?? [];
  } catch {
    return [];
  }
}
